import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Instagram } from "lucide-react";
import { cn } from "../../lib/utils";

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { name: "Ana Sayfa", path: "/" },
    { 
      name: "Kurumsal", 
      path: "#",
      dropdown: [
        { name: "Hakkımızda", path: "/kurumsal/hakkimizda" },
        { name: "Yönetim Kurulu", path: "/kurumsal/yonetim-kurulu" },
        { name: "Misyon & Vizyon", path: "/kurumsal/misyon-vizyon" },
        { name: "Kalite Politikamız", path: "/kurumsal/kalite-politikasi" },
        { name: "İnsan Kaynakları", path: "/kurumsal/insan-kaynaklari" },
      ]
    },
    { name: "Haberler", path: "/haberler" },
    { name: "Galeri", path: "/galeri" },
    { name: "İletişim", path: "/iletisim" },
  ];

  const isHome = location.pathname === "/";
  const needsDarkText = isScrolled || !isHome || mobileMenuOpen;

  return (
    <header
      className={cn(
        "fixed top-0 left-0 right-0 z-50 transition-all duration-500",
        needsDarkText
          ? "bg-white/95 backdrop-blur-lg border-b border-gray-200 py-3 shadow-sm"
          : "bg-transparent py-5"
      )}
    >
      <div className="container mx-auto px-6 max-w-7xl">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img 
              src="https://isilkoop.com/assets/images/logo/logo-dark.png" 
              alt="İSİLKOOP Logo" 
              className={cn(
                "h-10 md:h-12 object-contain transition-all duration-500",
                !needsDarkText ? "brightness-0 invert" : ""
              )}
            />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((link, idx) => (
              <div 
                key={idx} 
                className="relative group"
                onMouseEnter={() => setActiveDropdown(idx)}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                {link.dropdown ? (
                  <div className={cn(
                    "flex items-center gap-1 cursor-pointer text-sm font-semibold transition-colors hover:text-blue-500 py-2",
                    needsDarkText ? "text-slate-700" : "text-white"
                  )}>
                    {link.name}
                    <ChevronDown size={16} className={cn("transition-transform duration-200", activeDropdown === idx ? "rotate-180" : "")} />
                  </div>
                ) : (
                  <Link
                    to={link.path}
                    className={cn(
                      "text-sm font-semibold transition-colors hover:text-blue-500 relative py-2 block",
                      needsDarkText ? "text-slate-700" : "text-white"
                    )}
                  >
                    {link.name}
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-blue-500 transition-all group-hover:w-full"></span>
                  </Link>
                )}

                {/* Desktop Dropdown */}
                {link.dropdown && (
                  <div className={cn(
                    "absolute top-full left-0 w-60 bg-white rounded-xl shadow-xl border border-slate-100 py-3 transition-all duration-300 transform origin-top-left",
                    activeDropdown === idx ? "opacity-100 scale-100 pointer-events-auto" : "opacity-0 scale-95 pointer-events-none"
                  )}>
                    {link.dropdown.map((dropItem, dIdx) => (
                      <Link
                        key={dIdx}
                        to={dropItem.path}
                        className="block px-5 py-2.5 text-sm font-medium text-slate-700 hover:bg-blue-50 hover:text-blue-600 transition-colors"
                      >
                        {dropItem.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            
            <div className="flex items-center gap-4 ml-4 pl-4 border-l border-white/20">
              <a 
                href="https://instagram.com/isilkoop" 
                target="_blank" 
                rel="noreferrer"
                className={cn(
                  "transition-colors hover:text-pink-500",
                  needsDarkText ? "text-slate-700" : "text-white"
                )}
              >
                <Instagram size={20} />
              </a>
              <Link
                to="/iletisim"
                className={cn(
                  "px-6 py-2.5 rounded-full text-sm font-bold transition-all duration-300 shadow-md",
                  needsDarkText
                    ? "bg-primary text-white hover:bg-blue-600 hover:shadow-lg"
                    : "bg-white text-primary hover:bg-gray-100 hover:scale-105"
                )}
              >
                Bize Ulaşın
              </Link>
            </div>
          </nav>

          {/* Mobile Menu Toggle */}
          <button
            className={cn(
              "lg:hidden p-2 transition-colors",
              needsDarkText ? "text-primary" : "text-white"
            )}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </div>

      {/* Mobile Nav */}
      <div
        className={cn(
          "lg:hidden absolute top-full left-0 right-0 bg-white border-b shadow-2xl overflow-hidden transition-all duration-300 ease-in-out",
          mobileMenuOpen ? "max-h-screen opacity-100 border-t" : "max-h-0 opacity-0"
        )}
      >
        <div className="flex flex-col p-4 space-y-1">
          {navLinks.map((link, idx) => (
            <div key={idx}>
              {link.dropdown ? (
                <div className="flex flex-col">
                  <button 
                    onClick={() => setActiveDropdown(activeDropdown === idx ? null : idx)}
                    className="flex items-center justify-between w-full py-3 px-4 text-slate-800 font-bold hover:bg-slate-50 rounded-lg text-left"
                  >
                    {link.name}
                    <ChevronDown size={18} className={cn("transition-transform", activeDropdown === idx ? "rotate-180" : "")} />
                  </button>
                  <div className={cn(
                    "overflow-hidden transition-all duration-300",
                    activeDropdown === idx ? "max-h-[400px]" : "max-h-0"
                  )}>
                    <div className="bg-slate-50 rounded-lg mx-4 mb-2 mt-1 p-2 space-y-1">
                      {link.dropdown.map((dropItem, dIdx) => (
                        <Link
                          key={dIdx}
                          to={dropItem.path}
                          onClick={() => setMobileMenuOpen(false)}
                          className="block py-2.5 px-4 text-sm text-slate-600 hover:text-blue-600 font-semibold rounded-md hover:bg-white transition-colors"
                        >
                          {dropItem.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <Link
                  to={link.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className="block py-3 px-4 text-slate-800 font-bold hover:bg-slate-50 rounded-lg"
                >
                  {link.name}
                </Link>
              )}
            </div>
          ))}
          <div className="p-4 mt-2 flex flex-col gap-4">
            <a 
              href="https://instagram.com/isilkoop" 
              target="_blank" 
              rel="noreferrer"
              className="flex items-center justify-center gap-2 py-3 px-4 bg-pink-50 text-pink-600 font-bold rounded-lg"
            >
              <Instagram size={20} /> Instagram'da Takip Et
            </a>
            <Link
              to="/iletisim"
              onClick={() => setMobileMenuOpen(false)}
              className="block w-full py-3 px-4 bg-primary text-white text-center font-bold rounded-lg shadow-md"
            >
              Bize Ulaşın
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
